import { useCallback } from 'react';

import { IParsedContent } from '@/src/modules/CampaignsModule/components/actionManagement/WhatsAppAction/hooks/useContentVariables';
import {
  IWhatsAppTemplate,
  WhatsAppAction,
} from '@/src/modules/CampaignsModule/interfaces/WhatsApp';

export function useWhatsAppTemplate() {
  /**
   * Converts the result of parseAndReplaceContentVariables to a format
   * compatible with IWhatsAppTemplate for the PhonePreviewWhatsAppV0 component
   */
  const convertToWhatsAppTemplate = useCallback(
    (result: IParsedContent): { template: IWhatsAppTemplate | null; canUseTemplateV0: boolean } => {
      if (!result.isValid || !result.contentSid) {
        return { template: null, canUseTemplateV0: false };
      }

      let canUseTemplateV0 = false;
      let template: IWhatsAppTemplate | null = null;

      if (result.types) {
        template = {
          to: '',
          from: '',
          ContentSid: result.contentSid,
          friendly_name: '',
          language: '',
          variables: {},
          types: result.types,
        };
        canUseTemplateV0 = true;
      } else if (result.body) {
        template = {
          to: '',
          from: '',
          ContentSid: result.contentSid,
          friendly_name: '',
          language: '',
          variables: {},
          types: {
            'twilio/text': {
              body: result.body,
            },
          },
        };

        if (result.mediaUrl) {
          if (typeof result.mediaUrl === 'string') {
            template.types['twilio/media'] = {
              body: result.body,
              media: [result.mediaUrl],
            };
          } else if (Array.isArray(result.mediaUrl)) {
            template.types['twilio/media'] = {
              body: result.body,
              media: result.mediaUrl,
            };
          }
        }

        if (result.actions) {
          template.types['whatsapp/card'] = {
            body: result.body,
            media: (() => {
              if (Array.isArray(result.mediaUrl)) {
                return result.mediaUrl;
              } else if (result.mediaUrl) {
                return [result.mediaUrl];
              } else {
                return [];
              }
            })(),
            header_text: result.header_text || '',
            footer: result.footer || '',
            actions: (() => {
              if (Array.isArray(result.actions)) {
                const actions = result.actions as WhatsAppAction[];
                return actions;
              } else {
                return [];
              }
            })(),
          };
        }

        canUseTemplateV0 = true;
      }

      return { template, canUseTemplateV0 };
    },
    []
  );

  /**
   * Gets the appropriate mediaUrl for the PhonePreviewWhatsApp component
   * from the result of parseAndReplaceContentVariables
   */
  const getMediaUrl = useCallback((result: IParsedContent): string | undefined => {
    if (Array.isArray(result.mediaUrl) && result.mediaUrl.length > 0) {
      return result.mediaUrl[0];
    } else if (typeof result.mediaUrl === 'string') {
      return result.mediaUrl;
    }
    return undefined;
  }, []);

  return {
    convertToWhatsAppTemplate,
    getMediaUrl,
  };
}
